import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPut, queryDelete, queryPostFormData } from '../utils/request'

export interface Branding {
  subdomain: string | null
  display_name: string | null
  logo_light_url: string | null
  logo_dark_url: string | null
  primary_color: string | null
  is_default: boolean
  is_active?: boolean
}

export const useBrandingQuery = (tenantId: MaybeRefOrGetter<number | null>) => {
  return useQuery({
    queryKey: computed(() => queryKeys.brandingDetail(toValue(tenantId)!)),
    queryFn: async (): Promise<Branding> => {
      return queryGet<Branding>('/v2/vendor/branding', {
        params: { tenant_id: toValue(tenantId) },
      })
    },
    enabled: computed(() => !!toValue(tenantId)),
  })
}

export const useUpdateBrandingMutation = (tenantId: MaybeRefOrGetter<number | null>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: Partial<Branding>): Promise<Branding> => {
      return queryPut<Branding>('/v2/vendor/branding', payload, {
        params: { tenant_id: toValue(tenantId) },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.branding() })
    },
  })
}

export const useUploadLogoMutation = (tenantId: MaybeRefOrGetter<number | null>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ variant, file }: { variant: string; file: File }): Promise<Branding> => {
      const formData = new FormData()
      formData.append('file', file)
      return queryPostFormData<Branding>(`/v2/vendor/branding/logo/${variant}`, formData, {
        params: { tenant_id: toValue(tenantId) },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.branding() })
    },
  })
}

export const useDeleteLogoMutation = (tenantId: MaybeRefOrGetter<number | null>) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variant: string): Promise<Branding> => {
      return queryDelete<Branding>(`/v2/vendor/branding/logo/${variant}`, {
        params: { tenant_id: toValue(tenantId) },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.branding() })
    },
  })
}
